/**
 * Ring Item State Types
 *
 * Live Firestore-based state for dashboard ring items.
 * Enables instant snooze/mute with onSnapshot listeners.
 *
 * Firestore paths:
 * - Team-level: /teams/{teamId}/ringItemState/{itemKey}
 * - User-level: /users/{uid}/ringItemState/{itemKey}
 *
 * Stable itemKey formats:
 * - task:team:{taskId}     - Team tasks
 * - task:user:{taskId}     - Personal tasks (ONLY user-scoped items)
 * - doorstroom:{vehicleId} - Doorstroom vehicles
 * - liquiditeit:{invoiceId} - Liquiditeit invoices
 * - invoiceReminder:{invoiceId} - Invoice reminders
 * - quoteFollowUp:{quoteId} - Quote follow-ups
 *
 * Scope rules (FINAL):
 * - Team scope: doorstroom, liquiditeit, invoiceReminder, quoteFollowUp, team tasks
 * - User scope: personal tasks ONLY
 */

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// Types
// ============================================================================

/**
 * Item types that can have ring state
 */
export type RingItemType =
  | 'task'
  | 'doorstroom'
  | 'liquiditeit'
  | 'invoiceReminder'
  | 'quoteFollowUp';

/**
 * Scope determines which collection the ring state is stored in
 */
export type RingStateScope = 'team' | 'user';

/**
 * Ring item state document shape (Firestore)
 */
export interface RingItemState {
  itemKey: string;
  itemType: RingItemType;
  entityId: string;
  scope: RingStateScope;
  snoozedUntil: Timestamp | null;
  muted: boolean;
  updatedAt: Timestamp;
  updatedBy: string;
}

/**
 * Ring item state for client-side use (with Date instead of Timestamp)
 */
export interface RingItemStateClient {
  itemKey: string;
  itemType: RingItemType;
  entityId: string;
  scope: RingStateScope;
  snoozedUntil: Date | null;
  muted: boolean;
  updatedAt: Date;
  updatedBy: string;
}

/**
 * Map of item keys to their state
 */
export type RingItemStateMap = Map<string, RingItemStateClient>;

// ============================================================================
// Item Key Helpers (SINGLE SOURCE OF TRUTH)
// ============================================================================

/**
 * Generate stable item key for team tasks
 * Format: task:team:{taskId}
 */
export function getTeamTaskKey(taskId: string): string {
  return `task:team:${taskId}`;
}

/**
 * Generate stable item key for personal/user tasks
 * Format: task:user:{taskId}
 */
export function getUserTaskKey(taskId: string): string {
  return `task:user:${taskId}`;
}

/**
 * Generate stable item key for tasks based on scope
 * SINGLE SOURCE OF TRUTH for task keys - use this everywhere
 * Format: task:team:{taskId} or task:user:{taskId}
 */
export function getTaskKey(taskId: string, scope: 'team' | 'user'): string {
  return scope === 'team' ? getTeamTaskKey(taskId) : getUserTaskKey(taskId);
}

/**
 * Generate stable item key for doorstroom items
 * Format: doorstroom:{vehicleId}
 */
export function getDoorstroomKey(vehicleId: string): string {
  return `doorstroom:${vehicleId}`;
}

/**
 * Generate stable item key for liquiditeit items
 * Format: liquiditeit:{invoiceId}
 */
export function getLiquiditeitKey(invoiceId: string): string {
  return `liquiditeit:${invoiceId}`;
}

/**
 * Generate stable item key for invoice reminders
 * Format: invoiceReminder:{invoiceId}
 */
export function getInvoiceReminderKey(invoiceId: string): string {
  return `invoiceReminder:${invoiceId}`;
}

/**
 * Generate stable item key for quote follow-up items
 * Format: quoteFollowUp:{quoteId}
 */
export function getQuoteFollowUpKey(quoteId: string): string {
  return `quoteFollowUp:${quoteId}`;
}

/**
 * Universal getItemKey helper - determines key based on item type and scope
 */
export function getItemKey(
  itemType: RingItemType,
  entityId: string,
  scope?: RingStateScope
): string {
  switch (itemType) {
    case 'task':
      return scope === 'team' ? getTeamTaskKey(entityId) : getUserTaskKey(entityId);
    case 'doorstroom':
      return getDoorstroomKey(entityId);
    case 'liquiditeit':
      return getLiquiditeitKey(entityId);
    case 'invoiceReminder':
      return getInvoiceReminderKey(entityId);
    case 'quoteFollowUp':
      return getQuoteFollowUpKey(entityId);
    default:
      throw new Error(`Unknown item type: ${itemType}`);
  }
}

/**
 * Parse item key to extract type, entity ID, and scope
 */
export function parseItemKey(itemKey: string): {
  itemType: RingItemType;
  entityId: string;
  scope: RingStateScope;
} | null {
  // Task keys have 3 parts: task:team:{id} or task:user:{id}
  if (itemKey.startsWith('task:')) {
    const parts = itemKey.split(':');
    if (parts.length !== 3) return null;
    const [, scopePart, entityId] = parts;
    if (scopePart !== 'team' && scopePart !== 'user') return null;
    return { itemType: 'task', entityId, scope: scopePart as RingStateScope };
  }

  // Other keys have 2 parts: type:{id}
  const parts = itemKey.split(':');
  if (parts.length !== 2) return null;

  const [type, entityId] = parts;
  const validTypes = ['doorstroom', 'liquiditeit', 'invoiceReminder', 'quoteFollowUp'];
  if (!validTypes.includes(type)) return null;

  // All non-task items are team-scoped (doorstroom, liquiditeit, invoiceReminder, quoteFollowUp)
  const scope: RingStateScope = 'team';

  return { itemType: type as RingItemType, entityId, scope };
}

/**
 * Get the target scope for an item type
 * FINAL SCOPE RULES:
 * - Team scope: doorstroom, liquiditeit, invoiceReminder, quoteFollowUp, team tasks
 * - User scope: personal tasks ONLY
 */
export function getTargetScope(itemType: RingItemType, isPersonalTask?: boolean): RingStateScope {
  // Personal tasks are the ONLY user-scoped items
  if (itemType === 'task' && isPersonalTask) return 'user';
  // Everything else is team-scoped
  return 'team';
}

// ============================================================================
// State Helpers
// ============================================================================

/**
 * Check if an item should be hidden based on its ring state
 */
export function isItemHidden(state: RingItemStateClient | undefined, now: Date): boolean {
  if (!state) return false;

  // Muted = permanently hidden ("Niet meer tonen")
  if (state.muted) return true;

  // Snoozed = hidden until timestamp
  if (state.snoozedUntil && state.snoozedUntil > now) return true;

  return false;
}

/**
 * Check if a ring state doc should be cleaned up (expired snooze, not muted)
 */
export function shouldCleanupState(state: RingItemStateClient, now: Date): boolean {
  // If muted, keep the doc
  if (state.muted) return false;

  // If snooze has expired, clean up
  if (state.snoozedUntil && state.snoozedUntil <= now) return true;

  // If no snooze and not muted, clean up (empty state)
  if (!state.snoozedUntil) return true;

  return false;
}

/**
 * Calculate tomorrow 09:00 local time
 */
export function getTomorrowMorning(): Date {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d;
}

/**
 * Convert Firestore RingItemState to client-side RingItemStateClient
 */
export function toClientState(doc: RingItemState, itemKey: string): RingItemStateClient {
  return {
    itemKey,
    itemType: doc.itemType,
    entityId: doc.entityId,
    scope: doc.scope,
    snoozedUntil: doc.snoozedUntil?.toDate() ?? null,
    muted: doc.muted ?? false,
    updatedAt: doc.updatedAt.toDate(),
    updatedBy: doc.updatedBy,
  };
}
